/**
 * Backend'in GERÇEK piyasa uçlarına tek giriş noktası.
 *
 * Ağustos 2026 denetimi: japan/china/korea/india/europe servisleri backend'de hiç
 * var olmayan /api/jp, /api/hk, /api/kr, /api/in, /api/eu uçlarına istek atıyordu.
 * Hatalar boş nesneye çevrildiği için sayfalar HTTP 200 dönüp kalıcı skeleton
 * gösteriyordu — ne uptime izlemesi ne kullanıcı hata mesajı devreye giriyordu.
 * (robinhood.ts daha önce gerçek uçlara taşınmış, bu beşi atlanmıştı.)
 *
 * Backend'de gerçekten var olan uçlar:
 *   GET /api/market/quotes?symbols=A,B      → MarketQuoteRaw[]
 *   GET /api/stock/chart/{symbol}?period=X  → { data: [{ time, close, ... }] }
 */

import { fetchFromApi } from '@/lib/api'

/** Backend /api/market/quotes yanıtının ham biçimi. */
export interface MarketQuoteRaw {
  symbol: string
  name?: string
  currency?: string
  date?: string
  open: number
  high: number
  low: number
  close: number
  previousClose: number | null
  volume: number
  diff: number
  changePercent: number
  monthly: number | null
  yearly: number | null
}

interface StockChartRaw {
  symbol: string
  count: number
  data: Array<{ time: string; open: number; high: number; low: number; close: number; volume: number }>
}

export interface ChartPoint {
  time: number
  value: number
}

export interface PeriodChange {
  monthly: number
  yearly: number
}

/** Sayısal alanların her zaman sonlu bir sayı olmasını garanti eder (NaN/null ekrana sızmasın). */
export function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : fallback
}

function toEpochSeconds(iso?: string): number {
  if (!iso) return 0
  const ms = Date.parse(iso)
  return Number.isNaN(ms) ? 0 : Math.floor(ms / 1000)
}

/**
 * Sembol listesi için ham quote'ları çeker. Boş liste → hiç istek atmaz.
 * Hata durumunda boş nesne döner; çağıran taraf bunu "veri yok" olarak yorumlar.
 */
export async function fetchRawQuotes(symbols: string[]): Promise<Record<string, MarketQuoteRaw>> {
  const clean = symbols.filter(Boolean)
  if (clean.length === 0) return {}

  const raw = await fetchFromApi<MarketQuoteRaw[]>(
    `/api/market/quotes?symbols=${clean.map(encodeURIComponent).join(',')}`,
  )
  if (!Array.isArray(raw)) return {}

  return raw.reduce<Record<string, MarketQuoteRaw>>((acc, item) => {
    if (item?.symbol) acc[item.symbol] = item
    return acc
  }, {})
}

/** Grafik verisi — backend'in OHLC serisini kapanış fiyatı çizgisine indirger. */
export async function fetchChartSeries(symbol: string, period: string): Promise<ChartPoint[]> {
  if (!symbol) return []

  const raw = await fetchFromApi<StockChartRaw>(
    `/api/stock/chart/${encodeURIComponent(symbol)}?period=${encodeURIComponent(period)}`,
  )
  if (!raw || !Array.isArray(raw.data)) return []

  return raw.data
    .map(p => ({ time: toEpochSeconds(p.time), value: num(p.close) }))
    .filter(p => p.time > 0)
}

/** Aylık/yıllık değişim — aynı quote yanıtından türetilir, ek istek atılmaz. */
export async function fetchPeriodChanges(symbols: string[]): Promise<Record<string, PeriodChange>> {
  const quotes = await fetchRawQuotes(symbols)
  return Object.entries(quotes).reduce<Record<string, PeriodChange>>((acc, [symbol, q]) => {
    acc[symbol] = { monthly: num(q.monthly), yearly: num(q.yearly) }
    return acc
  }, {})
}

/** Asya piyasaları (JP/HK/KR/IN) için ortak quote biçimi. */
export interface AsiaQuote {
  symbol: string
  name: string
  price: number
  open: number
  dayHigh: number
  dayLow: number
  previousClose: number
  change: number
  changePercent: number
  volume: number
  lastUpdate: number
}

export function mapToAsiaQuote(raw: MarketQuoteRaw): AsiaQuote {
  return {
    symbol: raw.symbol,
    name: raw.name || raw.symbol,
    price: num(raw.close),
    open: num(raw.open),
    dayHigh: num(raw.high),
    dayLow: num(raw.low),
    previousClose: num(raw.previousClose),
    change: num(raw.diff),
    changePercent: num(raw.changePercent),
    volume: num(raw.volume),
    lastUpdate: toEpochSeconds(raw.date),
  }
}

/** Asya servislerinin (japan/china/korea/india) paylaştığı quote çekici. */
export async function fetchAsiaQuotes(symbols: string[]): Promise<Record<string, AsiaQuote>> {
  const raw = await fetchRawQuotes(symbols)
  return Object.entries(raw).reduce<Record<string, AsiaQuote>>((acc, [symbol, q]) => {
    acc[symbol] = mapToAsiaQuote(q)
    return acc
  }, {})
}
